/**
 * Tour engine helpers — the DOM-facing logic kept out of the React component
 * so it can be unit tested without mounting driver.js.
 */
import { onTutorialActionResult } from './tutorialOutcomes';

/** Normalizes a step's `selector` (string | string[]) into an array. */
export const selectorList = (selector) => {
  if (Array.isArray(selector)) return selector.filter(Boolean);
  return selector ? [selector] : [];
};

export const ACTION_SUCCESS = 'success';
export const ACTION_FAILURE = 'failure';
export const ACTION_TIMEOUT = 'timeout';

/**
 * Waits for a real UI action to settle.
 *
 * TRI-STATE, deliberately: `'success' | 'failure' | 'timeout'`. This used to
 * resolve a bare boolean, which collapsed "the action reported failure" and "we
 * gave up waiting" into the same `false` — and the caller treated that as failure
 * and tore the tour down. So an archive that SUCCEEDED but took longer than the
 * bound lost a completion the user had genuinely earned: the record was archived,
 * the success toast showed, and the checklist still said unfinished. The three
 * outcomes want three different responses, so they are three different values.
 */
export const waitForTutorialActionResult = (action, { timeout = 8000 } = {}) => {
  if (!action) return Promise.resolve(ACTION_SUCCESS);

  return new Promise((resolve) => {
    let timer;
    const unsubscribe = onTutorialActionResult((settledAction, success) => {
      if (settledAction !== action) return;
      unsubscribe();
      clearTimeout(timer);
      resolve(success ? ACTION_SUCCESS : ACTION_FAILURE);
    });

    timer = setTimeout(() => {
      unsubscribe();
      resolve(ACTION_TIMEOUT);
    }, timeout);
  });
};

/**
 * True when an element is actually on screen. driver.js will happily spotlight
 * a 0x0 or `display: none` node, which looks like a broken tour, so steps whose
 * target is hidden are treated as missing.
 */
export const isElementVisible = (element) => {
  if (!element || typeof element.getBoundingClientRect !== 'function') return false;
  const rect = element.getBoundingClientRect();
  // Zero in EITHER dimension: a 0-width anchor (an empty link cell) still
  // reports a nonzero height, but there is nothing to spotlight or click.
  if (!rect.width || !rect.height) return false;
  const doc = element.ownerDocument;
  const view = doc && doc.defaultView;
  if (view && typeof view.getComputedStyle === 'function') {
    const style = view.getComputedStyle(element);
    if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
  }
  return true;
};

/**
 * Widens a resolved target to the enclosing widget named by `step.expandTo`.
 *
 * antd composite inputs spread unknown props (our `data-tutorial`) onto the inner
 * control while putting `className` on an outer wrapper, so the attribute lands on
 * a fragment of the widget. `Input.Search` is the clear case: the attribute is on
 * the `<input>`, so the spotlight cut out the text field and left the search and
 * clear buttons outside it — visibly clipping the control it was pointing at.
 *
 * `closest` includes the element itself, so this is a no-op for steps whose
 * selector already matched the wrapper.
 */
const expandToWidget = (step, element) => {
  const selector = step && step.expandTo;
  if (!selector || !element || typeof element.closest !== 'function') return element;
  try {
    const wrapper = element.closest(selector);
    return wrapper && isElementVisible(wrapper) ? wrapper : element;
  } catch {
    // An invalid selector must never take the tour down.
    return element;
  }
};

/**
 * True when an element is disabled, in any of the ways this app expresses it.
 *
 * antd renders a disabled `Menu.Item` as an `<li>` with
 * `ant-dropdown-menu-item-disabled` and `aria-disabled`, not a `disabled`
 * attribute, so a plain `[disabled]` check misses exactly the case that matters.
 */
export const isElementDisabled = (element) => {
  if (!element) return false;
  if (element.disabled === true) return true;
  if (element.getAttribute && element.getAttribute('aria-disabled') === 'true') return true;
  const disabledClass = /(^|\s)(ant-[a-z-]*-disabled|ant-btn-disabled)(\s|$)/;
  if (element.className && typeof element.className === 'string'
    && disabledClass.test(element.className)) return true;
  // A disabled antd Button renders the attribute on the inner <button>.
  if (typeof element.querySelector === 'function' && element.querySelector('button[disabled]')) return true;
  return false;
};

/**
 * Returns the first visible element matching any of a step's selectors.
 *
 * With `step.requireEnabled`, disabled matches are skipped. Some records simply
 * cannot be assigned (a reply, a Jotform submission, or a multi-selection all
 * disable Assign Document), and highlighting a greyed-out menu item told the user
 * to click something that could never respond.
 */
export const resolveStepElement = (step, root = document) => {
  if (!root || typeof root.querySelectorAll !== 'function') return null;
  const candidates = selectorList(step && step.selector);
  const requireEnabled = !!(step && step.requireEnabled);

  for (let i = 0; i < candidates.length; i += 1) {
    let matches = [];
    try {
      matches = Array.from(root.querySelectorAll(candidates[i]));
    } catch {
      // An invalid selector should never take the whole tour down.
      matches = [];
    }
    const usable = matches.find((el) => (
      isElementVisible(el) && !(requireEnabled && isElementDisabled(el))
    ));
    if (usable) return expandToWidget(step, usable);
  }
  return null;
};

/**
 * Checks a step's `gate` before the tour is allowed to leave it.
 *
 * Needed for steps whose real precondition is the state the user put the page
 * into, not the presence of an element. The assign-record tutorial is the case
 * that forced it: "Pick a record" is satisfied by ticking ANY checkbox, but a
 * reply, a Jotform submission and a multi-selection all disable Assign Document —
 * so the tour used to sail on and dead-end two steps later on a greyed-out menu
 * item. The gate keeps the user on the step with an explanation instead.
 *
 * Returns `null` when the step may be left, or the reason string when it may not.
 * A gate that throws is treated as passing: a broken check must never make a
 * tutorial impossible to finish.
 */
export const evaluateStepGate = (step, root = document) => {
  const gate = step && step.gate;
  if (!gate || typeof gate.check !== 'function') return null;
  try {
    return gate.check(root) ? null : (gate.message || null);
  } catch {
    return null;
  }
};

/**
 * Drops steps whose target is missing *and* which are marked `optional`.
 * A required step is kept: the runner waits for it to appear (e.g. an action
 * button that only renders once the user selects a record).
 */
export const filterRunnableSteps = (steps = [], root = document) => (
  steps.filter((step) => {
    if (!step) return false;
    if (!step.optional) return true;
    return !!resolveStepElement(step, root);
  })
);

/**
 * A tutorial can start when its first step's target is present. Later steps are
 * allowed to be absent because they typically appear as a result of earlier
 * clicks (modals, dropdown menus, selection-gated buttons).
 */
export const canStartTutorial = (tutorial, root = document) => {
  if (!tutorial || !Array.isArray(tutorial.steps) || !tutorial.steps.length) return false;
  if (tutorial.available === false) return false;
  return !!resolveStepElement(tutorial.steps[0], root);
};

/**
 * Resolves a step's target, waiting for it to appear.
 *
 * Targets frequently show up only as a consequence of the previous step's real
 * click (a modal, a dropdown menu, or an action button that only renders once a
 * record is selected), so the runner waits before moving the spotlight rather
 * than highlighting an element that isn't there yet. Resolves `null` on timeout
 * so the caller can skip the step instead of hanging.
 */
export const waitForStepElement = (step, options = {}) => {
  const {
    timeout = 8000,
    interval = 100,
    root = document,
    now = () => Date.now(),
    // Optional cancellation. The interval below is not registered in
    // TutorialRunner's `timersRef`, so before this an abandoned wait kept polling
    // the DOM (a `querySelectorAll` per selector plus `getComputedStyle` per match,
    // every 100ms) for the full 8s after the tour was already gone — and nothing
    // could stop it. Callers that can be torn down pass a signal.
    signal = null,
  } = options;

  const existing = resolveStepElement(step, root);
  if (existing) return Promise.resolve(existing);
  if (signal && signal.aborted) return Promise.resolve(null);

  return new Promise((resolve) => {
    const startedAt = now();
    let timer = null;
    const finish = (value) => {
      if (timer) clearInterval(timer);
      resolve(value);
    };
    // Abortion is checked on each tick rather than through an 'abort' listener:
    // happy-dom's AbortSignal has no addEventListener, so subscribing threw and took
    // the whole tour down in tests. Polling the flag costs one property read per
    // interval and works on every implementation.
    timer = setInterval(() => {
      if (signal && signal.aborted) {
        finish(null);
        return;
      }
      const found = resolveStepElement(step, root);
      if (found) {
        finish(found);
        return;
      }
      if (now() - startedAt >= timeout) finish(null);
    }, interval);
  });
};

/** Centre point of an element in viewport coordinates, for the fake cursor. */
export const elementCenter = (element) => {
  if (!element || typeof element.getBoundingClientRect !== 'function') return null;
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + (rect.width / 2),
    y: rect.top + (rect.height / 2),
  };
};

/**
 * Maps our step config onto driver.js `DriveStep`s.
 *
 * Notes on the driver.js options that matter here:
 * - `disableActiveInteraction: false` keeps the highlighted element clickable,
 *   which is the whole point of an interactive tutorial.
 * - `showButtons` omits 'next' on click-advanced steps so the tooltip can't be
 *   used to skip past the action the step is teaching, but always keeps
 *   'close' so there is a visible exit on every step.
 * - `waitForElement` lets a required target appear late (modal, dropdown,
 *   selection-gated button) without failing the tour.
 */
export const buildDriverSteps = (steps = [], { waitForElement = 8000 } = {}) => (
  steps.map((step, index) => {
    const advanceOnClick = step.advanceOn === 'click';
    const candidates = selectorList(step.selector);
    // Prefer handing driver.js a plain CSS selector: that is its primary,
    // best-tested code path and it resolves + highlights reliably even when the
    // previous step's element was destroyed by the click that advanced us. The
    // lazy function form is only needed for steps that declare fallback
    // selectors (see RECORD_CHECKBOX_SELECTOR), where the winning selector
    // isn't known until run time.
    const element = candidates.length === 1
      ? candidates[0]
      : () => resolveStepElement(step) || undefined;
    return {
      // Never falls back to document.body: driver.js would then treat the whole
      // page as the highlighted element, and its
      // `.driver-active * { pointer-events: none }` rule leaves only <body>
      // clickable — the overlay swallows every click and the step becomes
      // impossible to complete.
      element,
      disableActiveInteraction: false,
      waitForElement,
      popover: {
        title: step.title,
        description: advanceOnClick
          ? `${step.body}`
          : step.body,
        side: step.side || 'bottom',
        align: step.align || 'start',
        // 'close' is always present: every step must be exitable.
        showButtons: advanceOnClick
          ? ['previous', 'close']
          : ['previous', 'next', 'close'],
        progressText: `Step ${index + 1} of ${steps.length}`,
        showProgress: true,
      },
      // Carried through so the runner can wire click-advance + the fake cursor.
      data: { step },
    };
  })
);
